/**
 * @module HypothesisAbsenceList
 * @purpose Renders expected-but-absent evidence entries for a hypothesis card.
 * @depends types/reasoning (HypothesisAbsenceEntry), react-i18next
 * @dependedOnBy HypothesisCard
 *
 * @rules
 * - Performance: pure display — props only, no state
 * - State: none
 * - Errors: n/a — purely presentational
 */

import { useTranslation } from 'react-i18next'
import type { HypothesisAbsenceEntry } from '../../../../../types/reasoning'

interface HypothesisAbsenceListProps {
  entries: HypothesisAbsenceEntry[]
}

interface AbsenceEntryProps {
  entry: HypothesisAbsenceEntry
}

const CONTAINER_STYLE: React.CSSProperties = {
  borderTop: '1px solid var(--border)',
  padding: '10px 0',
}

const SECTION_HEADING: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.62rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-muted)',
  marginBottom: '6px',
}

const OBSERVABLE_ID_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.72rem',
  color: 'var(--text)',
  marginBottom: '2px',
}

const CHIP_BASE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.58rem',
  padding: '1px 6px',
  borderRadius: '4px',
  display: 'inline-block',
  marginRight: '4px',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
  background: 'transparent',
}

const META_ROW: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.75rem',
  color: 'var(--text-dim)',
  marginTop: '2px',
}

const META_LABEL: React.CSSProperties = {
  color: 'var(--text-muted)',
}

const EMPTY_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.78rem',
  color: 'var(--text-muted)',
  fontStyle: 'italic',
}

const ENTRY_CONTAINER: React.CSSProperties = {
  padding: '6px 0',
  borderBottom: '1px solid var(--border)',
}

const TEMPORAL_NOTE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.6rem',
  color: 'var(--text-muted)',
  marginTop: '2px',
}

function AbsenceEntry({ entry }: AbsenceEntryProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const stateLabel = entry.state === 'absent'
    ? t('reasoning.hypothesis.absenceList.stateAbsent')
    : t('reasoning.hypothesis.absenceList.stateNotCollected')

  return (
    <div style={ENTRY_CONTAINER}>
      <div style={OBSERVABLE_ID_STYLE}>{entry.observable}</div>
      <span style={CHIP_BASE}>{stateLabel}</span>
      <div style={META_ROW}>
        <span style={META_LABEL}>{t('reasoning.hypothesis.absenceList.significanceLabel')}: </span>
        {entry.significance}
      </div>
      <div style={META_ROW}>
        <span style={META_LABEL}>{t('reasoning.hypothesis.absenceList.whyExpectedLabel')}: </span>
        {entry.why_expected}
      </div>
      {entry.temporal_limitation_applies && (
        <div style={TEMPORAL_NOTE}>{t('reasoning.hypothesis.absenceList.temporalLimitation')}</div>
      )}
    </div>
  )
}

/**
 * Renders the expected-but-absent evidence inventory for a hypothesis.
 *
 * @behaviour Shows empty message when no absent entries; otherwise renders one row per entry
 *   with observable ID, state badge, significance, why-expected, and temporal limitation note.
 * @errors n/a
 */
export function HypothesisAbsenceList({ entries }: HypothesisAbsenceListProps): React.ReactElement {
  const { t } = useTranslation('analysis')

  return (
    <div style={CONTAINER_STYLE}>
      <div style={SECTION_HEADING}>{t('reasoning.hypothesis.absenceList.title')}</div>
      {entries.length === 0
        ? <div style={EMPTY_STYLE}>{t('reasoning.hypothesis.absenceList.empty')}</div>
        : entries.map((entry) => <AbsenceEntry key={entry.observable} entry={entry} />)
      }
    </div>
  )
}
